/* src/shared/areaPick.js — "tick a whole area" quick-pick for the audience
   pickers on News, Tasks and Surveys (v126.57).

   Matt: "on admin/news - when publishing can we have an area selecter? same
   with tasks and surveys?"

   DESIGN (Matt's call): this is a SNAPSHOT, not a new audience kind. Clicking an
   area ticks that area's stores in the existing "Selected stores" list, and the
   item is still saved as audience='stores' with an explicit list. Nothing about
   how the Worker decides who sees what changes — no migration, no new visibility
   rule to get wrong. The trade-off, accepted knowingly: a store that moves INTO
   an area later does not retroactively join posts or recurring tasks published
   before the move. If that ever bites, a live 'area' audience is the follow-up.

   Because it only ticks checkboxes you can also mix — take a whole area, then
   untick one store, or add a store from elsewhere. A radio button could not.

   WHY SHARED: the three admin bundles are otherwise self-contained, so this is
   the first cross-bundle import in the frontend. It is deliberate. Earlier today
   the same "just add another copy" instinct had produced THREE separate store
   dropdowns (rota, logs, then the topbar), which drifted apart until one of them
   sent an area manager to the wrong store. Twenty lines duplicated three times
   is how that starts. esbuild inlines this into each bundle; there is no new
   script tag and no new global.

   Reads the admin.html inline globals (ALL_STORES, ALL_AREAS, getStoreArea) by
   bare name, guarded — the classic-script contract every bundle already uses. */

// Areas that actually have stores, as [{ id, name, stores: [name...] }].
// An area with no stores is not offered — an empty chip is a dead control.
export function areasWithStores() {
  var names = [];
  try {
    if (typeof ALL_STORES !== 'undefined' && Array.isArray(ALL_STORES)) {
      names = ALL_STORES.map(function (s) { return typeof s === 'string' ? s : (s && s.name); }).filter(Boolean);
    }
  } catch (e) { /* globals not ready */ }
  var byArea = {};
  names.forEach(function (n) {
    var a = '';
    try { if (typeof getStoreArea === 'function') a = String(getStoreArea(n) || ''); } catch (e) { /* ignore */ }
    if (!a) return;                       // unassigned / virtual stores stay out
    (byArea[a] = byArea[a] || []).push(n);
  });
  var areaName = function (id) {
    try {
      if (typeof ALL_AREAS !== 'undefined' && Array.isArray(ALL_AREAS)) {
        var f = ALL_AREAS.find(function (x) { return String(x.id) === String(id); });
        if (f && f.name) return String(f.name);
      }
    } catch (e) { /* ignore */ }
    return 'Area ' + id;                  // last resort only; the NAME is the label
  };
  return Object.keys(byArea).sort().map(function (id) {
    return { id: id, name: areaName(id), stores: byArea[id].sort() };
  });
}

// The chip row. `g` is the bundle's global name (NewsAdmin / TasksAdmin /
// SurveysAdmin) so the onclick reaches the right module.
export function areaChipsHtml(g) {
  var areas = areasWithStores();
  if (!areas.length) return '';
  var chip = 'border:1px solid var(--border,#d1d7db);background:var(--card);border-radius:14px;padding:4px 10px;'
    + 'font-size:12px;cursor:pointer;font-family:inherit;color:var(--text);margin:0 6px 6px 0';
  return '<div class="ap-areas" style="display:flex;flex-wrap:wrap;align-items:center;margin-bottom:6px">'
    + '<span style="font-size:12px;color:var(--sub,#54656f);margin:0 8px 6px 0">Quick pick:</span>'
    + areas.map(function (a) {
      return '<button type="button" style="' + chip + '" onclick="' + g + '.pickArea(\''
        + String(a.id).replace(/'/g, '') + '\')">'
        + escapeApHtml(a.name) + ' (' + a.stores.length + ')</button>';
    }).join('')
    + '</div>';
}

// Toggle: if every store in the area is already ticked, untick them; otherwise
// tick them all. Leaves stores outside the area exactly as they were, so chips
// compose — two areas, or an area plus a one-off store.
export function pickAreaStores(cbClass, areaId) {
  var area = areasWithStores().find(function (a) { return String(a.id) === String(areaId); });
  if (!area) return;
  var boxes = [];
  try { boxes = Array.prototype.slice.call(document.querySelectorAll('.' + cbClass)); } catch (e) { return; }
  var inArea = boxes.filter(function (b) { return area.stores.indexOf(b.value) !== -1; });
  if (!inArea.length) return;
  var allOn = inArea.every(function (b) { return b.checked; });
  inArea.forEach(function (b) { b.checked = !allOn; });
}

// Local escape — this module must not depend on a host page helper.
function escapeApHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
    return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
  });
}
